import { useState, useEffect } from "react";
import { api, detailText, formatMediaUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  Plus,
  Pencil,
  Trash2,
  Upload,
  Loader2,
  Check,
  X,
  Image as ImageIcon,
  HelpCircle,
  Key,
  ShieldCheck,
  Eye,
  EyeOff,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const CATEGORIES = ["Adat", "Studio", "Modern", "Islamic", "Outdoor", "Minimalist"];

export default function AdminPreweddingTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sampleVer, setSampleVer] = useState(Date.now());

  // Modal State Form Create / Edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form inputs
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Studio");
  const [description, setDescription] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Upload sample image
  const [uploadingSampleId, setUploadingSampleId] = useState(null);

  // Delete confirm modal
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Gemini API Key State
  const [geminiKeyInput, setGeminiKeyInput] = useState("");
  const [maskedKey, setMaskedKey] = useState("");
  const [hasKey, setHasKey] = useState(false);
  const [keySource, setKeySource] = useState("");
  const [keyModel, setKeyModel] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [showPlainKey, setShowPlainKey] = useState(false);

  // Saringan bentuk kunci di sisi klien — sama dengan validasi backend, supaya
  // salah-tempel ditolak instan tanpa perlu round-trip jaringan.
  const looksLikeGeminiKey = (v) => {
    const s = (v || "").trim().replace(/^["']|["']$/g, "");
    if (s.length < 30) return false;
    return s.startsWith("AIza") || s.startsWith("AQ.");
  };

  const fetchGeminiKey = async () => {
    try {
      const res = await api.get("/admin/settings/gemini-key");
      setHasKey(Boolean(res.data?.has_key));
      setMaskedKey(res.data?.masked_key || "");
      setKeySource(res.data?.source || "");
      setKeyModel(res.data?.model || "");
    } catch (err) {
      // Quiet fallback
    }
  };

  const validateKeyInput = () => {
    const s = geminiKeyInput.trim();
    if (!s) {
      toast.error("Masukkan Google Gemini API Key terlebih dahulu");
      return false;
    }
    if (!looksLikeGeminiKey(s)) {
      toast.error("Format kunci tidak dikenali. Kunci Gemini diawali 'AIza' atau 'AQ.' dan panjangnya ≥ 30 karakter.");
      return false;
    }
    return true;
  };

  const handleSaveGeminiKey = async (e) => {
    e.preventDefault();
    if (!validateKeyInput()) return;
    setSavingKey(true);
    try {
      const res = await api.post("/admin/settings/gemini-key", {
        api_key: geminiKeyInput.trim(),
      });
      toast.success(res.data?.message || "Gemini API Key valid & berhasil disimpan!");
      setMaskedKey(res.data?.masked_key || "");
      setHasKey(true);
      setKeySource("admin");
      setGeminiKeyInput("");
      setShowKeyInput(false);
      setShowPlainKey(false);
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menyimpan Gemini API Key");
    } finally {
      setSavingKey(false);
    }
  };

  const handleTestGeminiKey = async () => {
    if (!validateKeyInput()) return;
    setTestingKey(true);
    try {
      const res = await api.post("/admin/settings/test-gemini-key", {
        api_key: geminiKeyInput.trim(),
      });
      if (res.data?.ok) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data?.message || "Pengujian kunci gagal");
      }
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Pengujian koneksi Gemini gagal");
    } finally {
      setTestingKey(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/prewedding-templates");
      setTemplates(res.data?.templates || []);
    } catch (err) {
      toast.error("Gagal memuat template AI prewedding");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchGeminiKey();
  }, []);

  const openCreateModal = () => {
    setEditingTemplate(null);
    setTitle("");
    setCategory("Studio");
    setDescription("");
    setPrompt(
      "High resolution romantic prewedding photo of an Indonesian couple in an elegant studio. Groom height: {groom_height}cm, weight: {groom_weight}kg. Bride height: {bride_height}cm, weight: {bride_weight}kg. Soft lighting, 8k portrait."
    );
    setIsActive(true);
    setModalOpen(true);
  };

  const openEditModal = (t) => {
    setEditingTemplate(t);
    setTitle(t.title || "");
    setCategory(t.category || "Studio");
    setDescription(t.description || "");
    setPrompt(t.prompt || "");
    setIsActive(t.is_active !== false);
    setModalOpen(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!title.trim() || !prompt.trim()) {
      toast.error("Judul dan Prompt Template wajib diisi");
      return;
    }

    setSubmitting(true);
    try {
      if (editingTemplate) {
        // Update
        const res = await api.patch(`/admin/prewedding-templates/${editingTemplate.template_id}`, {
          title: title.trim(),
          category,
          description: description.trim(),
          prompt: prompt.trim(),
          is_active: isActive,
        });
        toast.success("Template berhasil diperbarui");
      } else {
        // Create
        const res = await api.post("/admin/prewedding-templates", {
          title: title.trim(),
          category,
          description: description.trim(),
          prompt: prompt.trim(),
          is_active: isActive,
        });
        toast.success("Template baru berhasil ditambahkan");
      }
      setModalOpen(false);
      await fetchTemplates();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menyimpan template");
      console.error("Error saving template:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadSample = async (templateId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Ukuran sample foto maksimal 5 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploadingSampleId(templateId);
    try {
      await api.post(`/admin/prewedding-templates/${templateId}/sample`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Sample gambar template berhasil diunggah!");
      setSampleVer(Date.now());
      await fetchTemplates();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal mengunggah sample gambar");
    } finally {
      setUploadingSampleId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/prewedding-templates/${deleteTarget.template_id}`);
      toast.success("Template berhasil dihapus");
      setDeleteTarget(null);
      await fetchTemplates();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal menghapus template");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      {/* Header Admin */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-border/60">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-1.5 rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <Sparkles className="h-5 w-5" />
            </span>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold">Admin &bull; Template AI Prewedding</h1>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            Kelola template pose, sample gambar referensi, dan prompt AI Gemini yang dapat dipilih pengguna.
          </p>
        </div>

        <Button
          onClick={openCreateModal}
          className="rounded-xl font-semibold text-xs bg-primary text-primary-foreground flex items-center gap-1.5 shadow-sm"
        >
          <Plus className="h-4 w-4" /> Tambah Template
        </Button>
      </div>

      {/* Pengaturan Gemini API Key Card */}
      <Card className="rounded-2xl border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-card to-card p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-600 dark:text-amber-400 font-bold shrink-0">
              <Key className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-bold text-foreground">Pengaturan Google Gemini API Key</h3>
                {hasKey ? (
                  <Badge variant="secondary" className="text-[10px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-semibold border-none flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" /> Terpasang ({maskedKey})
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-500/40">
                    Belum Dikonfigurasi
                  </Badge>
                )}
                {hasKey && keySource === "admin" && (
                  <Badge variant="outline" className="text-[10px] border-border/70 text-muted-foreground">
                    Disimpan Admin
                  </Badge>
                )}
                {hasKey && keySource === "env" && (
                  <Badge variant="outline" className="text-[10px] border-border/70 text-muted-foreground">
                    Dari Environment
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Kunci API dipakai sistem Weddly untuk menganalisis foto mempelai &amp; menghasilkan gambar prewedding via Gemini AI.
                {keyModel && <span className="ml-1">Model aktif: <strong className="text-foreground">{keyModel}</strong>.</span>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowKeyInput(!showKeyInput);
                if (showKeyInput) { setGeminiKeyInput(""); setShowPlainKey(false); }
              }}
              className="rounded-xl text-xs"
            >
              {showKeyInput ? "Tutup Form" : hasKey ? "Ganti Kunci API" : "Masukkan API Key"}
            </Button>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleTestGeminiKey}
              disabled={testingKey || savingKey}
              className="rounded-xl text-xs"
            >
              {testingKey ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
              Uji Koneksi Gemini
            </Button>
          </div>
        </div>

        {/* Form Input Key */}
        {showKeyInput && (
          <form onSubmit={handleSaveGeminiKey} className="mt-4 pt-4 border-t border-border/60 space-y-3">
            <div>
              <label className="text-xs font-semibold text-foreground block mb-1">Google Gemini API Key (Google AI Studio)</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showPlainKey ? "text" : "password"}
                    value={geminiKeyInput}
                    onChange={(e) => setGeminiKeyInput(e.target.value)}
                    placeholder="Masukkan API Key (contoh: AIzaSy... atau AQ.Ab8...)"
                    autoComplete="off"
                    spellCheck={false}
                    className="w-full px-3 py-2 pr-10 text-xs rounded-xl border border-border bg-background font-mono"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPlainKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                    title={showPlainKey ? "Sembunyikan kunci" : "Tampilkan kunci"}
                  >
                    {showPlainKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </button>
                </div>
                <Button type="submit" size="sm" disabled={savingKey || testingKey} className="rounded-xl text-xs font-semibold px-4">
                  {savingKey ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                  Simpan Kunci
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                Dapatkan API Key gratis di <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-primary underline">aistudio.google.com</a>.
                Kunci divalidasi ke Google sebelum disimpan. Hanya kunci yang valid yang akan lolos.
              </p>
            </div>
          </form>
        )}
      </Card>

      {/* Grid Templates */}
      {loading ? (
        <div className="py-20 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 px-4 rounded-2xl border border-dashed border-border bg-card/30">
          <ImageIcon className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-foreground">Belum Ada Template Prewedding</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            Klik tombol Tambah Template untuk membuat template gaya AI baru.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {templates.map((t) => (
            <Card key={t.template_id} className="rounded-2xl border-border/60 bg-card/80 shadow-sm overflow-hidden flex flex-col justify-between">
              <div>
                {/* Sample Image Box */}
                <div className="relative aspect-[16/10] overflow-hidden bg-muted border-b border-border/60">
                  {t.has_sample && t.sample_url ? (
                    <img
                      src={`${formatMediaUrl(t.sample_url)}?v=${sampleVer}`}
                      alt={t.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground/50 bg-gradient-to-br from-amber-500/10 to-primary/10">
                      <Sparkles className="h-8 w-8 mb-1.5 text-primary/30" />
                      <span className="text-xs font-medium">Belum ada sample foto</span>
                    </div>
                  )}

                  <Badge className="absolute top-2.5 left-2.5 text-[10px] px-2 py-0.5 bg-black/60 backdrop-blur-md text-white border-none">
                    {t.category}
                  </Badge>

                  {/* Upload Sample Overlay Button */}
                  <label
                    className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-black/60 hover:bg-black/80 text-white text-[11px] font-medium backdrop-blur-md border border-white/20 cursor-pointer transition-colors shadow-sm"
                    title="Ganti sample gambar"
                  >
                    {uploadingSampleId === t.template_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                    <span>{t.has_sample ? "Ganti Sample" : "Unggah Sample"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleUploadSample(t.template_id, e.target.files?.[0])}
                    />
                  </label>
                </div>

                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-serif text-base font-semibold text-foreground truncate">{t.title}</h3>
                    <Badge variant={t.is_active ? "default" : "outline"} className="text-[10px]">
                      {t.is_active ? "Aktif" : "Nonaktif"}
                    </Badge>
                  </div>

                  <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                    {t.description || "Tidak ada deskripsi."}
                  </p>

                  <div className="mt-3 p-2.5 rounded-xl bg-muted/40 border border-border/50">
                    <span className="text-[10px] font-mono text-muted-foreground block mb-0.5 font-semibold">Prompt AI:</span>
                    <p className="text-[11px] font-mono text-foreground/80 line-clamp-3 leading-tight">{t.prompt}</p>
                  </div>
                </div>
              </div>

              {/* Actions Footer */}
              <div className="p-4 pt-2 border-t border-border/50 flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEditModal(t)}
                  className="flex-1 text-xs rounded-lg"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Template
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleteTarget(t)}
                  className="text-xs rounded-lg text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal Form Tambah / Edit Template */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-xl rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-serif">
              {editingTemplate ? "Edit Template AI Prewedding" : "Tambah Template AI Prewedding Baru"}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Tentukan judul, kategori, dan prompt dasar AI. Gunakan variabel &#123;groom_height&#125;, &#123;groom_weight&#125;, &#123;bride_height&#125;, &#123;bride_weight&#125; di dalam prompt.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveTemplate} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Judul Template</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Contoh: Korean Romantic Studio"
                required
                className="w-full px-3 py-2 text-xs rounded-xl border border-border bg-background"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Kategori</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-border bg-background"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-foreground block mb-1">Status</label>
                <select
                  value={isActive ? "1" : "0"}
                  onChange={(e) => setIsActive(e.target.value === "1")}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-border bg-background"
                >
                  <option value="1">Aktif (Dapat Dipilih)</option>
                  <option value="0">Nonaktif</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground block mb-1">Deskripsi Singkat</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Deskripsi tema untuk membantu pengguna memilih"
                className="w-full px-3 py-2 text-xs rounded-xl border border-border bg-background"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-foreground">Custom Prompt Gemini AI</label>
                <span className="text-[10px] text-muted-foreground">Variabel otomatis didukung</span>
              </div>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                required
                placeholder="Tulis prompt detail deskripsi busana, background, pencahayaan, dan variabel tinggi/berat..."
                className="w-full px-3 py-2 text-xs font-mono rounded-xl border border-border bg-background leading-relaxed"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Tips: Gunakan kata kunci 8k portrait photography, shallow depth of field, natural studio lighting.
              </p>
            </div>

            <DialogFooter className="flex justify-end gap-2 pt-3 border-t border-border/60">
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)} className="rounded-xl text-xs">
                Batal
              </Button>
              <Button type="submit" size="sm" disabled={submitting} className="rounded-xl text-xs font-semibold">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingTemplate ? "Simpan Perubahan" : "Buat Template"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal Konfirmasi Hapus */}
      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-destructive font-serif">Hapus Template Prewedding?</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground pt-1">
              Template <strong className="text-foreground">{deleteTarget?.title}</strong> akan dihapus permanen beserta sample fotonya.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} className="rounded-xl text-xs">
              Batal
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleting} className="rounded-xl text-xs">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Ya, Hapus Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
